/** Session list filtering and grouping. Pure functions plus a URL-backed filter hook. */
import type { Machine, NeedsYouSessionLevel, Project, Session, SessionStatus } from '@orgus/protocol';
import { SESSION_STATUSES, agentKindLabel, agentKindOf, isTestRun } from '@orgus/protocol';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { compareSessions, modelLabel } from './labels.ts';
import { shownStatus } from '../asks/levels.ts';
import { sessionOutcome } from './outcome.ts';
import type { OutcomeInput } from './outcome.ts';

/**
 * 'tests': runs a test, probe or experiment started (ADR 0059); every other filter leaves them out.
 * 'succeeded' and 'failed' narrow Ended to how a session came out (outcome.ts).
 */
export type StatusFilter = 'all' | 'live' | SessionStatus | 'succeeded' | 'failed' | 'tests';
export const STATUS_FILTERS: readonly StatusFilter[] = ['all', 'live', ...SESSION_STATUSES, 'succeeded', 'failed', 'tests'];

export interface SessionFilters {
  status: StatusFilter;
  machineId: string;
  projectId: string;
  /** An agent kind ('claude-code', 'codex', ...), or '' for every agent. */
  agentKind?: string;
  q: string;
}

export const NO_FILTERS: SessionFilters = { status: 'all', machineId: '', projectId: '', agentKind: '', q: '' };

export function isFiltered(f: SessionFilters): boolean {
  return f.status !== 'all' || !!f.machineId || !!f.projectId || !!f.agentKind || !!f.q.trim();
}

/** The agent kinds present in a list, Claude Code first, then by name. */
export function agentKindsIn(list: readonly Pick<Session, 'agentKind'>[]): string[] {
  const kinds = new Set(list.map((s) => agentKindOf(s)));
  return [...kinds].sort((a, b) => Number(b === 'claude-code') - Number(a === 'claude-code') || agentKindLabel(a).localeCompare(agentKindLabel(b)));
}

/**
 * Whether a session shows under a status filter. With `levels` (the needsYou selector), a waiting
 * session whose question is old, dismissed or snoozed files under Idle, not Needs you (ADR 0056).
 */
export function matchesStatus(s: Pick<Session, 'id' | 'status'> & OutcomeInput, f: StatusFilter, levels?: ReadonlyMap<string, NeedsYouSessionLevel>): boolean {
  // Test, probe and experiment runs show only under Tests (ADR 0059 decision 4).
  if (f === 'tests' || isTestRun(s)) return f === 'tests' && isTestRun(s);
  if (f === 'succeeded' || f === 'failed') return sessionOutcome(s) === f;
  return f === 'all' || (f === 'live' ? s.status !== 'ended' : shownStatus(s, levels) === f);
}

export interface Lookup {
  projects: Record<string, Project>;
  machines: Record<string, Machine>;
  /** Each waiting session's place in Needs you (the needsYou selector), for the status filter. */
  levels?: ReadonlyMap<string, NeedsYouSessionLevel>;
}

/** Every field a person might type to find a session: title, last text, path, model, project, machine, children. */
export function searchText(s: Session, l: Lookup): string {
  return [
    s.title,
    s.lastText,
    s.cwd,
    s.model ?? '',
    modelLabel(s.model),
    agentKindLabel(s.agentKind),
    l.projects[s.projectId ?? '']?.name ?? '',
    l.machines[s.machineId]?.name ?? '',
    ...s.subagents.map((a) => `${a.type} ${a.description}`),
    ...s.workflows.map((w) => `${w.name} ${w.description}`),
  ]
    .join('\n')
    .toLowerCase();
}

/** `anyStatus`: every status, test runs included (the page counts each status from this). */
export function filterSessions(list: Session[], f: SessionFilters, l: Lookup, opts: { anyStatus?: boolean } = {}): Session[] {
  const terms = f.q.toLowerCase().split(/\s+/).filter(Boolean);
  return list.filter(
    (s) =>
      (opts.anyStatus || matchesStatus(s, f.status, l.levels)) &&
      (!f.machineId || s.machineId === f.machineId) &&
      (!f.agentKind || agentKindOf(s) === f.agentKind) &&
      (!f.projectId || (f.projectId === 'none' ? s.projectId == null : s.projectId === f.projectId)) &&
      (!terms.length || terms.every((t) => searchText(s, l).includes(t))),
  );
}

export interface MachineGroup {
  machineId: string;
  sessions: Session[];
}

export interface ProjectGroup {
  /** '' for sessions outside any watched project. */
  projectId: string;
  /** Every session of the project, what needs you first (compareSessions), across machines. */
  sessions: Session[];
  /** The same sessions split by machine. */
  machines: MachineGroup[];
  count: number;
  lastActivity: number;
}

/** Group by project (and, inside, by machine). Projects with something waiting come first, then by recent activity; no project last. */
export function groupSessions(list: Session[], l: Lookup): ProjectGroup[] {
  const byProject = new Map<string, Map<string, Session[]>>();
  for (const s of list) {
    const pk = s.projectId ?? '';
    let m = byProject.get(pk);
    if (!m) byProject.set(pk, (m = new Map()));
    const arr = m.get(s.machineId);
    if (arr) arr.push(s);
    else m.set(s.machineId, [s]);
  }
  const groups: ProjectGroup[] = [];
  for (const [projectId, machines] of byProject) {
    const mg: MachineGroup[] = [...machines.entries()]
      .map(([machineId, sessions]) => ({ machineId, sessions: [...sessions].sort(compareSessions) }))
      .sort(
        (a, b) =>
          Number(!!l.machines[b.machineId]?.online) - Number(!!l.machines[a.machineId]?.online) ||
          (l.machines[a.machineId]?.name ?? '').localeCompare(l.machines[b.machineId]?.name ?? ''),
      );
    const all = mg.flatMap((g) => g.sessions);
    groups.push({
      projectId,
      sessions: [...all].sort(compareSessions),
      machines: mg,
      count: all.length,
      lastActivity: Math.max(...all.map((s) => (s.status === 'waiting' ? Number.MAX_SAFE_INTEGER : s.lastActivity))),
    });
  }
  return groups.sort(
    (a, b) =>
      Number(a.projectId === '') - Number(b.projectId === '') ||
      b.lastActivity - a.lastActivity ||
      (l.projects[a.projectId]?.name ?? '').localeCompare(l.projects[b.projectId]?.name ?? ''),
  );
}

/**
 * The group order to show after a live update, given the order on screen (`shown`, project
 * ids). Groups keep their places; a group moves only when it starts or stops waiting for you
 * (waiting groups first), and new groups take their place from `next`. Activity alone never
 * moves a group: whole groups jumped as sessions in them did any work.
 */
export function holdGroupOrder(shown: readonly string[] | null, next: ProjectGroup[]): ProjectGroup[] {
  if (!shown?.length) return next;
  const at = new Map(shown.map((id, i) => [id, i]));
  const fresh = new Map(next.map((g, i) => [g.projectId, i]));
  const waiting = (g: ProjectGroup) => g.sessions.some((x) => x.status === 'waiting');
  const place = (g: ProjectGroup) => at.get(g.projectId) ?? shown.length + fresh.get(g.projectId)!;
  return [...next].sort(
    (a, b) => Number(a.projectId === '') - Number(b.projectId === '') || Number(waiting(b)) - Number(waiting(a)) || place(a) - place(b),
  );
}

/** Filters live in the address, so a filtered view can be linked and Back restores it. */
export function useSessionFilters(): [SessionFilters, (patch: Partial<SessionFilters>) => void, () => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('status');
  const filters = useMemo<SessionFilters>(
    () => ({
      status: (STATUS_FILTERS as readonly string[]).includes(raw ?? '') ? (raw as StatusFilter) : 'all',
      machineId: params.get('machine') ?? '',
      projectId: params.get('project') ?? '',
      agentKind: params.get('agent') ?? '',
      q: params.get('q') ?? '',
    }),
    [params, raw],
  );
  const set = useCallback(
    (patch: Partial<SessionFilters>) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          const put = (k: string, v: string | undefined, empty = '') => (v === undefined ? undefined : v && v !== empty ? p.set(k, v) : p.delete(k));
          put('status', patch.status, 'all');
          put('machine', patch.machineId);
          put('project', patch.projectId);
          put('agent', patch.agentKind);
          put('q', patch.q);
          return p;
        },
        { replace: true, preventScrollReset: true },
      ),
    [setParams],
  );
  const clear = useCallback(() => set({ status: 'all', machineId: '', projectId: '', agentKind: '', q: '' }), [set]);
  return [filters, set, clear];
}
